import {
  FiFile,
  FiFileText,
  FiImage,
  FiMail,
  FiMic,
  FiUpload,
} from 'react-icons/fi';
import type React from 'react';
import { cn } from '../../../../lib/utils';
import IconWrapper from '../../../ui/IconWrapper';
import type { EvidenceItem, EvidenceType } from '../../../../types/record';
import { useRecordEvidence } from './useRecordEvidence';

interface RecordEvidenceSectionProps {
  initialEvidence?: EvidenceItem[];
}

export const RecordEvidenceSection: React.FC<RecordEvidenceSectionProps> = ({
  initialEvidence = [],
}) => {
  const {
    evidence,
    isDragging,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileSelect,
  } = useRecordEvidence(initialEvidence);

  return (
    <div className="rounded-xl border border-border/70 bg-muted/20 p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between">
        <h5 className="text-base font-semibold text-foreground">Evidence</h5>
        {evidence.length > 0 && (
          <span className="text-sm text-muted-foreground">
            {evidence.length} {evidence.length === 1 ? 'file' : 'files'}
          </span>
        )}
      </div>

      {evidence.length > 0 && (
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {evidence.map((item) => {
            const EvidenceIcon = getEvidenceIcon(item.type);
            return (
              <div
                key={item.id}
                className="flex min-w-0 items-start gap-3 rounded-xl border border-border/70 bg-card p-3 transition-colors hover:bg-accent/30"
              >
                <div className="flex-shrink-0 rounded-xl border border-border/70 bg-muted/25 p-2">
                  <IconWrapper icon={EvidenceIcon} className="size-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium" title={item.filename}>
                    {item.filename}
                  </p>
                  {item.summary && (
                    <p className="mt-1 line-clamp-2 break-words text-xs text-muted-foreground">
                      {item.summary}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatFileSize(item.size)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'rounded-xl border-2 border-dashed p-4 transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-border/70 bg-card hover:border-primary/50'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf,application/json,text/plain,text/csv,audio/*"
          aria-label="Upload evidence files"
        />

        <div className="flex flex-col items-center justify-center text-center">
          <IconWrapper icon={FiUpload} className="mb-1.5 h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            Drag evidence here or{' '}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-primary hover:underline"
            >
              browse
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export const getEvidenceIcon = (type: EvidenceType) => {
  switch (type) {
    case 'document':
      return FiFileText;
    case 'image':
      return FiImage;
    case 'audio':
      return FiMic;
    case 'email':
      return FiMail;
    case 'transcript':
      return FiFile;
    default:
      return FiFile;
  }
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
